import { CommonModule, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filterNullUndefined } from '@domgen/dgx-fe-common';
import { ButtonComponent } from '@domgen/dgx-fe-components/button';
import { CardComponent } from '@domgen/dgx-fe-components/card';
import { IconComponent } from '@domgen/dgx-fe-components/icon';
import { GAClickDirective, GAEvent, GAService } from '@domgen/dgx-fe-ga';
import { TranslatePipe } from '@domgen/dgx-fe-i18n';
import { BehaviorSubject, debounceTime, filter, map } from 'rxjs';
import { FeedbackVote } from './feedback-vote.enum';
import { SmartFixHubAnalyticsService } from 'libs/self-fix/data-access/src/lib/services/sf-hub-analytics/sf-hub-analytics-service';

@Component({
  selector: 'feedback-smart-fix-hub',
  standalone: true,
  templateUrl: './feedback.component.html',
  styleUrls: ['./feedback.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NgIf, NgFor, IconComponent, ButtonComponent],
  providers: [SmartFixHubAnalyticsService],
})
export class FeedbackComponent implements OnInit {
  @Input() feedbackQuestion? = 'Did this resolve the issue?';
  @Input() confirmationLink? = '/';

  constructor(
    private cd: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
    private readonly smartFixHubAnalyticsService: SmartFixHubAnalyticsService
  ) {}

  currentVoteSubject = new BehaviorSubject<boolean | undefined>(undefined);
  votedYes = false;
  votedNo = false;
  selectedOption: any;
  isError = false;
  isSubmitted = false;
  eventCategory = 'sf-hub';
  eventAction = 'article feedback';
  votingOptions = [
    { vote: true, label: FeedbackVote.PositiveVote, icon: 'thumbs-up' },
    { vote: false, label: FeedbackVote.NegativeVote, icon: 'thumbs-down' },
  ];

  ngOnInit(): void {
    this.currentVoteSubject
      .pipe(
        filterNullUndefined(),
        map((vote) => {
          this.updateVotingIcons(vote);
          return vote;
        }),
        debounceTime(1000),
        takeUntilDestroyed(this.destroyRef)
      )
      .pipe(filter(() => this.votedYes || this.votedNo))
      .subscribe(() => {
        this.cd.markForCheck();
      });
  }

  onVote(option: any): void {
    this.isError = false;
    this.selectedOption = option;
    this.currentVoteSubject.next(option.vote);
  }

  onClick(): void {
    if (this.selectedOption) {
      this.isError = false;
      this.isSubmitted = true;

      this.smartFixHubAnalyticsService.trackEvent({
        event: 'genericGAEvent',
        eventCategory: this.eventCategory,
        eventAction: this.eventAction,
        eventLabel:
          this.selectedOption.label === FeedbackVote.PositiveVote
            ? FeedbackVote.PositiveVote
            : FeedbackVote.NegativeVote,
      });
      this.onVote(this.selectedOption);
    } else {
      this.isError = true;
    }
  }

  updateVotingIcons(vote: boolean): void {
    this.votedYes = vote ? !this.votedYes : false;
    this.votedNo = vote ? false : !this.votedNo;
  }
}
